/**
 * <custom-quick-links>
 *
 * Umschliesst das <quick-links>-Element aus sections/custom-quick-links.liquid und
 * ergaenzt die sticky Sprungnavigation um drei Dinge:
 *
 *   1. Scroll-Spy — der Link des gerade sichtbaren Abschnitts bekommt aria-current,
 *      das Styling dazu steht in assets/custom-quick-links.css.
 *   2. --quick-links-height auf :root, damit scroll-padding-top die Hoehe der
 *      Leiste mitrechnen kann und Sprungziele nicht dahinter landen.
 *   3. [data-scroll-end] auf der Liste, damit die Fade-Kante auf Mobile
 *      verschwindet, sobald ganz nach rechts gescrollt wurde.
 *
 * Die Sticky-Position selbst kommt aus dem CSS ueber --header-end, das
 * store-header (assets/main.js) live pflegt — hier wird sie nur gelesen.
 */
if (!customElements.get('custom-quick-links')) {
  class CustomQuickLinks extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('.quick-links__list');

      // Sticky ist das Section-Element, nicht dieses hier — Hoehe und Grenzlinie
      // muessen das Padding aus .section--padded-* einschliessen.
      this.bar = this.closest('.cc-quick-links') || this;

      // Nur interne Sprunglinks bekommen einen aktiven Zustand — externe Links
      // und Blocks ohne URL bleiben aussen vor.
      this.links = Array.from(this.querySelectorAll('a[href^="#"]'))
        .map((link) => ({
          link,
          target: document.getElementById(decodeURIComponent(link.hash.slice(1))),
        }))
        .filter((entry) => entry.target);

      this.handleResize = this.handleResize.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleListScroll = this.handleListScroll.bind(this);

      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(this.bar);

      if (this.links.length) {
        this.addEventListener('click', this.handleClick);
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        this.updateActiveLink();
      }

      if (this.list) {
        this.list.addEventListener('scroll', this.handleListScroll, { passive: true });
        this.handleListScroll();
      }
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.removeEventListener('click', this.handleClick);
      window.removeEventListener('scroll', this.handleScroll);
      this.list?.removeEventListener('scroll', this.handleListScroll);
      if (this.frame) cancelAnimationFrame(this.frame);
      document.documentElement.style.removeProperty('--quick-links-height');
    }

    /**
     * Untere Kante der Leiste im Viewport. Solange sie sticky klebt, ist das die
     * Linie, ab der ein Abschnitt als "aktuell" gilt. Vor dem Kleben liegt sie
     * weiter unten — dann ist ohnehin noch kein Sprungziel passiert.
     * @returns {number}
     */
    get boundary() {
      return this.bar.getBoundingClientRect().bottom;
    }

    /**
     * Drosselt die Auswertung auf einen Frame pro Scroll-Burst.
     */
    handleScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updateActiveLink();
      });
    }

    /**
     * Setzt aria-current auf den letzten Link, dessen Ziel oberhalb der
     * Leistenunterkante liegt. Liegt noch keiner darueber, bleibt der erste aktiv.
     */
    updateActiveLink() {
      const boundary = this.boundary + 1;
      let activeIndex = 0;

      // Erst alle Positionen lesen, dann schreiben — kein Layout-Thrashing.
      this.links.forEach(({ target }, index) => {
        if (target.getBoundingClientRect().top <= boundary) activeIndex = index;
      });

      this.setActiveLink(activeIndex);
    }

    /**
     * @param {number} activeIndex - Index in this.links.
     */
    setActiveLink(activeIndex) {
      if (activeIndex === this.activeIndex) return;
      this.activeIndex = activeIndex;

      this.links.forEach(({ link }, index) => {
        if (index === activeIndex) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    }

    /**
     * Klick auf einen Tab markiert ihn sofort, ohne aufs Scrollen zu warten.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      const link = evt.target.closest('a[href^="#"]');
      if (!link) return;

      const index = this.links.findIndex((entry) => entry.link === link);
      if (index !== -1) this.setActiveLink(index);
    }

    /**
     * Haelt --quick-links-height aktuell — Grundlage fuer scroll-padding-top in
     * assets/custom-quick-links.css.
     */
    handleResize() {
      const height = Math.round(this.bar.getBoundingClientRect().height);
      if (height === this.lastHeight) return;

      this.lastHeight = height;
      document.documentElement.style.setProperty('--quick-links-height', `${height}px`);
    }

    /**
     * Blendet die Fade-Kante aus, sobald die Liste ganz nach rechts gescrollt ist.
     */
    handleListScroll() {
      const remaining = this.list.scrollWidth - this.list.clientWidth - this.list.scrollLeft;
      this.list.toggleAttribute('data-scroll-end', remaining <= 1);
    }
  }

  customElements.define('custom-quick-links', CustomQuickLinks);
}
